import { get, filter, compose } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, number, ord, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { getStringOrNullProp, getStringProp } from '@mews/fp';
import { OrderItemType, ProductPricing } from '@mews-commander/data-access';
import { composeProps, createPropsExtender } from '@mews/framework';
import { Product } from '@mews-commander/types';
import {
    getProductChargingTextKeyForBookableServices,
    getProductPostingTextKeyForBookableServices,
} from '@mews/business-components';
import { getProduct, getProducts } from '../ducks/products';
import { getServicePerTimeUnitKey, getServicePerTimeUnitLocalization } from '../utils/serviceTimeUnitLocalization';
import { localizedEntities } from '../ducks/utils';
import { getOrderItemsByIds } from '../ducks';

export interface ProductChargingLocalization {
    perTimeUnitText: string;
    textKey: Mews.TextKey | null;
}

type ProductWithName = Product & {
    name?: string;
};

const getProductChargingMode = createSelector(getProduct, get('chargingMode'));
const getProductPostingMode = createSelector(getProduct, get('postingMode'));
const getProductServiceId = createSelector(getProduct, get('serviceId'));

// @ts-expect-error $TSFixMeError: Parameter 'selector' implicitly has an 'any' type.
const createProductServiceIdExtender = (selector) =>
    composeProps(selector, createPropsExtender('id', getProductServiceId));

export const getProductChargingLocalization = createSelector(
    createProductServiceIdExtender(getServicePerTimeUnitKey),
    createProductServiceIdExtender(getServicePerTimeUnitLocalization),
    getProductChargingMode,
    (perTimeUnitKey, perTimeUnitText, chargingMode): ProductChargingLocalization => ({
        // @ts-expect-error $TSFixMeError: Type 'unknown' is not assignable to type 'string'.
        perTimeUnitText,
        textKey: chargingMode
            ? getProductChargingTextKeyForBookableServices(chargingMode, perTimeUnitKey as Mews.TextKey)
            : null,
    })
);

export const getProductPostingLocalization = createSelector(
    createProductServiceIdExtender(getServicePerTimeUnitKey),
    createProductServiceIdExtender(getServicePerTimeUnitLocalization),
    getProductPostingMode,
    (perTimeUnitKey, perTimeUnitText, postingMode): ProductChargingLocalization => ({
        // @ts-expect-error $TSFixMeError: Type 'unknown' is not assignable to type 'string'.
        perTimeUnitText,
        textKey: postingMode
            ? getProductPostingTextKeyForBookableServices(postingMode, perTimeUnitKey as Mews.TextKey)
            : null,
    })
);

export const getServiceProducts = createSelector(getProducts, getStringProp('serviceId'), (products, serviceId) =>
    filter({ serviceId, isDeleted: false }, products)
);

export const getServiceAbsolutelyPricedProducts = compose(
    filter(({ pricing }) => pricing.discriminator === ProductPricing.Absolute),
    getServiceProducts
);

export const getNonDefaultServiceAbsolutelyPricedProducts = compose(
    filter(({ isDefault }) => !isDefault),
    getServiceAbsolutelyPricedProducts
);

export const getServicePriceableProductIds = compose(
    array.map(({ id }) => id),
    getNonDefaultServiceAbsolutelyPricedProducts
);

const sortProductsByOrdering = pipe(
    number.Ord,
    ord.contramap((p: Product) => p.ordering)
);

const sortProductsByName = pipe(
    string.Ord,
    ord.contramap((p: ProductWithName) => p.name || '')
);

export const getServicePriceableProductIdsByProductCategoryIdSortedByOrdering = createSelector(
    localizedEntities(getNonDefaultServiceAbsolutelyPricedProducts, 'names', 'name'),
    getStringOrNullProp('productCategoryId'),
    (products, productCategoryIdProp) => {
        const nullableProductCategoryId =
            !productCategoryIdProp || productCategoryIdProp === '' ? null : productCategoryIdProp;

        return pipe(
            products,
            array.filter(({ productCategoryId }) => productCategoryId === nullableProductCategoryId),
            array.filter(({ options }) => options.offerToEmployee),
            array.sortBy([sortProductsByOrdering, sortProductsByName]),
            array.map(({ id }) => id)
        );
    }
);

export const getProductPricing = createSelector(getProduct, get('pricing'));

export const getProductIdsByOrderItemIds = createSelector(
    getOrderItemsByIds,
    flow(
        array.map(({ data }) => (data.discriminator === OrderItemType.ProductOrder ? data.value.productId ?? '' : '')),
        array.filter((value) => !!value),
        array.uniq(string.Eq)
    )
);
